/**
 * Review page · "Add CV" / "Replace CV" — upload a CV for this candidate, with
 *   ☐ Evaluate CV after upload
 * On: the server stores it, extracts the text and scores it against the requirement; the page then
 * shows the new score. Off: the CV is stored and extracted only — no evaluation, no score.
 *
 *   Replace (a CV is on file):  PATCH /requisitions/{req}/resumes/{scan}  { resumeFile, reScore }
 *   Add (none yet):             POST  /requisitions/{req}/resumes  { resumes, skipScoring }  → the
 *                               normal stored + billed upload — then POST /candidacies/{id}/resume/attach
 * Same visible states as the Add Candidate modal (CvUploadStatus).
 */
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { UploadCloud } from "lucide-react";
import { api, apiUpload, ApiError, fileToBase64 } from "../../lib/api.js";
import { CvUploadStatus, cvProblem, type CvUploadItem } from "./CvUploadStatus.js";

export function CvAddReplace({
  candidacyId,
  requisitionId,
  scanId,
  hasCv,
  onDone,
}: {
  candidacyId: string;
  requisitionId: string;
  /** The CV on file (replace); null = none yet (add). */
  scanId: string | null;
  hasCv: boolean;
  onDone: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [evaluate, setEvaluate] = useState(true);
  const [item, setItem] = useState<CvUploadItem | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const busy = !!item && (item.phase === "reading" || item.phase === "uploading" || item.phase === "processing");
  const replacing = hasCv && !!scanId;
  const label = replacing ? "Replace CV" : "Add CV";
  const patch = (p: Partial<CvUploadItem>): void => setItem((cur) => (cur ? { ...cur, ...p } : cur));

  async function upload(file: File | undefined): Promise<void> {
    if (!file || busy) return;
    const it: CvUploadItem = { id: String(Date.now()), name: file.name, phase: "reading", progress: 0 };
    setItem(it);
    const problem = cvProblem(file);
    if (problem) {
      setItem({ ...it, phase: "failed", message: problem });
      return;
    }
    const hooks = {
      onProgress: (f: number) => patch({ progress: f }),
      onUploaded: () => patch({ phase: "processing" }),
    };
    try {
      const contentBase64 = await fileToBase64(file);
      patch({ phase: "uploading", progress: 0 });
      const resumeFile = { filename: file.name, mime: file.type || "application/octet-stream", contentBase64 };
      let score: number | null = null;
      if (replacing) {
        const res = await apiUpload<{ scan: { overall: number | null } }>(
          "PATCH",
          `/requisitions/${requisitionId}/resumes/${scanId}`,
          { resumeFile, reScore: evaluate },
          hooks,
        );
        score = res.scan.overall;
      } else {
        const res = await apiUpload<{ scans: { id: string; overall: number | null }[]; duplicates: number }>(
          "POST",
          `/requisitions/${requisitionId}/resumes`,
          { resumes: [resumeFile], skipScoring: !evaluate },
          hooks,
        );
        const scan = res.scans[0];
        if (!scan) {
          throw new ApiError(409, res.duplicates > 0 ? "This exact CV is already on file for this requirement." : "The server didn't store the CV.");
        }
        await api.post(`/candidacies/${candidacyId}/resume/attach`, { scanId: scan.id });
        score = scan.overall;
      }
      if (evaluate && score == null) {
        patch({ phase: "failed", message: "The CV was stored, but the AI couldn't evaluate it (not charged). Use Run AI match to retry." });
      } else {
        patch({
          phase: "done",
          score: evaluate ? score : null,
          message: evaluate ? "Stored and evaluated against the requirement." : "Stored — not evaluated.",
        });
      }
      onDone();
    } catch (err) {
      patch({ phase: "failed", message: err instanceof Error ? err.message : "Couldn't upload the CV. Please retry." });
      onDone();
    }
  }

  return (
    <>
      <button type="button" className="btn" onClick={() => { setItem(null); setOpen(true); }}>
        <UploadCloud size={15} aria-hidden="true" /> {label}
      </button>
      {open && (
        <div className="rq-modal-scrim" role="dialog" aria-modal="true" aria-label={label} onClick={busy ? undefined : () => setOpen(false)}>
          <div className="rq-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="rq-modal-h">
              <div className="t">{label}</div>
              <div className="d">
                {replacing ? "The new file replaces the CV on file for this candidate." : "Attach a CV to this candidate."}
              </div>
            </div>
            <div className="rq-modal-b">
              <div
                className={`t1-cvdrop${dragging ? " is-drag" : ""}`}
                role="button"
                tabIndex={0}
                aria-label="Choose or drop a PDF / Word CV"
                onClick={() => !busy && input.current?.click()}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), !busy && input.current?.click())}
                onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false); }}
                onDrop={(e) => { e.preventDefault(); setDragging(false); void upload(e.dataTransfer.files[0]); }}
              >
                <input
                  ref={input}
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    void upload(f);
                  }}
                />
                <UploadCloud style={{ width: 32, height: 32, color: "var(--teal)", margin: "0 auto 10px" }} />
                <div style={{ fontWeight: 700, fontSize: 14 }}>{dragging ? "Drop to upload" : "Drop the CV here, or click to choose"}</div>
                <div className="mut sm" style={{ marginTop: 4 }}>PDF or Word · up to 10 MB</div>
              </div>
              <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 13.5, marginBottom: 12, cursor: busy ? "default" : "pointer" }}>
                <input type="checkbox" checked={evaluate} disabled={busy} onChange={(e) => setEvaluate(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <b>Evaluate CV after upload</b>
                  <span className="mut sm" style={{ display: "block" }}>
                    {evaluate
                      ? "The AI scores it against this requirement once it's uploaded — uses one resume from your plan."
                      : "Store the CV only — no evaluation, no score."}
                  </span>
                </span>
              </label>
              {item && <CvUploadStatus items={[item]} evaluating={evaluate} />}
            </div>
            <div className="rq-modal-f">
              <button type="button" className="rq-btn ghost" disabled={busy} onClick={() => setOpen(false)}>
                {item && !busy ? "Done" : "Cancel"}
              </button>
              <button type="button" className="rq-btn primary" disabled={busy} onClick={() => input.current?.click()}>
                {busy ? "Working…" : item?.phase === "failed" ? "Try another file" : "Choose file"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
